import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useChargingKeyActivateChargingKey } from 'api/charging-key/charging-key';
import Modal from 'components/Modal';
import UserPicker from 'components/UserPicker';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import useModalStack from 'contexts/modal/useModalStack';
import { FC, useState } from 'react';
import styled from 'styled-components';
import { chargingKeyLabel, toastChargingKeyResult } from './chargingKeyUtils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 300px;
`;

const Hint = styled.p`
  font-size: 12px;
  font-style: italic;
`;

interface Props {
  onUpdated?: () => void;
}

/**
 * Används från betalkortssidan. Användarkortet har en egen inline-aktivering — den här modalen
 * drar in UserPicker, som via UserModalLink importerar UserFormModal (cirkulärt därifrån).
 */
const ActivateChargingKeyModal: FC<Props> = ({ onUpdated }) => {
  const modalStack = useModalStack();
  const [uidOrVisualNumber, setUidOrVisualNumber] = useState('');
  const [cardHolderId, setCardHolderId] = useState<number | null>(null);
  const activateCall = useChargingKeyActivateChargingKey();

  const activate = async () => {
    try {
      const result = await activateCall.mutateAsync({
        data: {
          uidOrVisualNumber: uidOrVisualNumber.trim(),
          cardHolderID: cardHolderId ?? undefined,
        },
      });
      toastChargingKeyResult(
        result,
        `Laddnyckel ${chargingKeyLabel(result.key)} är aktiverad`
      );
      onUpdated?.();
      modalStack.pop();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  return (
    <Modal
      title="Aktivera laddnyckel"
      buttons={[
        {
          label: activateCall.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            'Aktivera'
          ),
          onClick: activate,
          disabled: activateCall.isLoading || !uidOrVisualNumber.trim(),
        },
        {
          label: 'Avbryt',
          onClick: () => modalStack.pop(),
        },
      ]}
    >
      <Wrapper>
        <LabelWrap label="Nummer på nyckeln">
          <Input
            autoFocus
            placeholder="t.ex. ZAKRA0001"
            value={uidOrVisualNumber}
            onChange={(e) => setUidOrVisualNumber(e.target.value)}
          />
        </LabelWrap>
        <LabelWrap label="Fälttestare (valfritt)">
          <UserPicker
            selectedUserId={cardHolderId}
            onUserPicked={(user) => setCardHolderId(user?.userID ?? null)}
            noneOption="Ingen"
          />
        </LabelWrap>
        <Hint>
          För nya nycklar som inte tagits i bruk än. Nycklar som redan finns på
          kontot hämtas med Synka laddnycklar och slås på när de tilldelas.
          Fortum tar månadsavgift för varje nyckel som varit påslagen under
          månaden.
        </Hint>
      </Wrapper>
    </Modal>
  );
};

export default ActivateChargingKeyModal;
